import { useState, useCallback } from 'react'
import {
  FormControl,
  FormLabel,
  RadioGroup,
  Typography,
  Box,
  styled,
} from '@mui/material'
import { HallType, InviteLink } from '@weblite/ts-action/hall'
import { REGEX_COMPILED } from '@weblite/permission'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { MenuIcon } from '$common/icons/menu'
import hostConf from '$config/frontend'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { Alert } from '$common/input/alert'
import { LinkInput } from '$common/input/link-input'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { useRoom } from '$logic/room/react-hook'
import { useHall, useHallInvitationLinks } from '$logic/hall/react-hook'
import {
  closeDefineHallAccessibility,
  closeEditHallAccessibility,
  openAddHallMembers,
} from '$logic/router'
import { makeHallPrivate, makeHallPublic } from '$logic/hall'
import { hallConstraints } from '$logic/hall/constant'
import { t } from '$setup/internationalization'
import { showLinkMenu } from '$comp/modals/menus/link-menu'
import { vibrate } from '$func/vibrate'
import { RadioButtonListItem } from './radio-button-list-item'

type InvitationLinkStatus =
  | 'AVAILABLE'
  | 'NOT_AVAILABLE'
  | 'INVALID'
  | 'LESS_THAN_5_CHAR'
  | 'NOT_START_WITH_LETTER'

const i18nMap: Record<InvitationLinkStatus, string> = {
  AVAILABLE: t('validation.invitationLink.isAvailable'),
  INVALID: t('validation.invitationLink.notSupportedChars'),
  LESS_THAN_5_CHAR: t('validation.invitationLink.leastChars'),
  NOT_START_WITH_LETTER: t('validation.invitationLink.notStartWithLetter'),
  NOT_AVAILABLE: t('validation.invitationLink.exists'),
}

const LinkSection = styled(FormControl)`
  margin-top: 10px;
  padding: 10px 20px 20px;
  box-sizing: border-box;
  background-color: white;
`

const HallAccessibilitySection = styled(FormControl)`
  padding: 10px 20px;
  background-color: white;
`

function SectionLabel({ text }: { text: string }) {
  return (
    <FormLabel>
      <Typography variant="subtitle3" color="primary">
        {text}
      </Typography>
    </FormLabel>
  )
}

interface Props {
  hallId: string
  hallType: HallType
  defaultPublic: boolean
  defaultIdentifier: string
  isHallCreation: boolean
  onBack: () => unknown
  onComplete: () => unknown
}

export function HallAccessibility({
  onBack,
  onComplete,
  hallType,
  defaultPublic,
  defaultIdentifier,
  isHallCreation,
  hallId,
}: Props) {
  const [isPublic, setIsPublic] = useState(defaultPublic)
  const [identifier, setIdentifier] = useState(defaultIdentifier)
  const [status, setStatus] = useState<InvitationLinkStatus>()

  const alert = status ? i18nMap[status] : null
  const variant = status === 'AVAILABLE' ? 'success' : 'error'
  const isRepetitive = status === 'NOT_AVAILABLE'

  const { items } = isHallCreation
    ? useHallInvitationLinks(hallId)
    : { items: [] as InviteLink[] }

  const invitationLink = items[0]?.link

  const handleConfirm = async () => {
    if (!isPublic) {
      await showSaveChangesLoadingModal()
      await makeHallPrivate(hallId)
      await closeCurrentModal()

      onComplete()
      return
    }

    if (identifier) {
      if (identifier.length && !/[a-z]/i.test(identifier[0]))
        setStatus('NOT_START_WITH_LETTER')
      else if (identifier.length && identifier.length < 5)
        setStatus('LESS_THAN_5_CHAR')
      else if (identifier.length && !REGEX_COMPILED.username.test(identifier))
        setStatus('INVALID')
      else {
        setStatus(undefined)

        await showSaveChangesLoadingModal()
        const res = await makeHallPublic(hallId, identifier)
        await closeCurrentModal()

        if (!res) {
          setStatus('NOT_AVAILABLE')
          vibrate()
        } else onComplete()
      }
    }
  }

  const openLinkMenu = useCallback(() => {
    showLinkMenu({
      link: `${hostConf.FRONT_HOST}/${isPublic ? identifier : invitationLink}`,
    })
  }, [identifier, invitationLink, isPublic])

  const canOpenLinkMenu =
    (!isPublic && invitationLink) ||
    (isPublic && defaultIdentifier && identifier === defaultIdentifier)

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={t(`hallAccessibility.hallSetting.${hallType.toLowerCase()}`)}
          onConfirm={handleConfirm}
          onBack={onBack}
          confirmDisabled={isPublic && !identifier}
        />
      }
    >
      <HallAccessibilitySection>
        <SectionLabel
          text={t(`hallAccessibility.type.${hallType.toLowerCase()}`)}
        />
        <RadioGroup
          defaultValue={defaultPublic ? 'public' : 'private'}
          name="radio-buttons-group"
        >
          {['private', 'public'].map(type => (
            <RadioButtonListItem
              hallType={hallType}
              handleIsPublic={() => setIsPublic(type === 'public')}
              accessibility={type}
              key={type}
            />
          ))}
        </RadioGroup>
      </HallAccessibilitySection>

      {(isPublic || isHallCreation) && (
        <LinkSection>
          <SectionLabel
            text={t(
              `hallAccessibility.link.${isPublic ? 'public' : 'private'}`,
            )}
          />

          <LinkInput
            sx={{ marginTop: '10px' }}
            identifier={isPublic ? identifier : invitationLink}
            isRepetitive={isRepetitive}
            handleChange={(newValue: string) => {
              setStatus(undefined)
              setIdentifier(newValue)
            }}
            readOnly={!isPublic}
            maxLength={hallConstraints.link.maxLength}
            endIcon={canOpenLinkMenu ? <MenuIcon /> : undefined}
            onEndIconClick={openLinkMenu}
          />

          {isPublic && (
            <>
              {alert && <Alert text={alert} variant={variant} />}
              <Box sx={{ marginTop: '8px' }}>
                {['legalCharacters', 'minimumLength'].map(rule => (
                  <Typography
                    variant="caption1"
                    color="gray"
                    display="inline-block"
                    key={rule}
                  >
                    {t(`hallAccessibility.publicLinkHint.${rule}`)}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </LinkSection>
      )}
    </PageWithAppBar>
  )
}

export function DefineHallAccessibility({ roomId }: { roomId: string }) {
  const room = useRoom(roomId)

  if (!room) return null

  return (
    <HallAccessibility
      hallId={roomId}
      isHallCreation
      hallType={room.type as HallType}
      defaultIdentifier=""
      defaultPublic={false}
      onBack={closeDefineHallAccessibility}
      onComplete={() => openAddHallMembers(roomId, true, true)}
    />
  )
}

export function EditHallAccessibility({ roomId }: { roomId: string }) {
  const { entity: hall } = useHall(roomId)
  const room = useRoom(roomId)

  if (!room || !hall) return null

  return (
    <HallAccessibility
      hallId={roomId}
      isHallCreation={false}
      hallType={room.type as HallType}
      defaultIdentifier={hall.identifier}
      defaultPublic={hall.public}
      onBack={closeEditHallAccessibility}
      onComplete={closeEditHallAccessibility}
    />
  )
}
